import React, {createContext, useContext, useState} from 'react';
import {pageView} from 'hooks/usePageView';

type DialogContextType<T> = {
  state: DialogState<T>;
  setState: React.Dispatch<React.SetStateAction<DialogState<T>>>;
  onClose: () => void;
};

export type DialogState<T> = (T & {type: DialogTypes}) | null;

export type DialogTypes =
  | 'demo'
  | 'html'
  | 'send-money'
  | 'flex-offer'
  | 'postpone-offer'
  | 'payment-free-month'
  | 'connect-account'
  | 'ks-ppi-anytime'
  | 'activate-card'
  | 'pre-collection'
  | 'invoice-process-info'
  | 'send-secure-message'
  | 'feedback'
  | 'offer'
  | 'digital-invoice-notification'
  | 'digital-invoice-notification-force'
  | 'update-contact-information'
  | 'verify-contact'
  | 'accounts-savings-close'
  | 'savings-withdrawal'
  | 'deposit-actions'
  | 'deposit-direct-transfer'
  | 'trustpilot-review'
  | 'deposit-monthly-deposit'
  | 'popup';

const DialogContext = createContext<DialogContextType<unknown> | undefined>(
  undefined,
);

export const useDialogProvider = <T,>() => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within an DialogContext');
  }
  return context as DialogContextType<T>;
};

export const DialogProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [state, setState] = useState<DialogState<unknown>>(null);
  const setStateWithAnalytics = (
    action: React.SetStateAction<DialogState<unknown>>,
  ) => {
    setState((prev) => {
      const next = typeof action === 'function' ? action(prev) : action;
      if (prev?.type !== next?.type) {
        if (prev?.type)
          pageView(`dialog_${prev.type.replaceAll('-', '_')}_close`);

        if (next?.type)
          pageView(`dialog_${next.type.replaceAll('-', '_')}_open`);
      }
      return next;
    });
  };

  const onClose = () => setStateWithAnalytics(null);

  return (
    <DialogContext.Provider
      value={{onClose, state, setState: setStateWithAnalytics}}>
      {children}
    </DialogContext.Provider>
  );
};
